import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type { CustomerAuthStateType } from '@redux/types'
import { Customer, CustomerType } from '@schemas'
import { ModalInfoType } from '@components/molecules/modal/Modal'

// initial state
const initialState: CustomerAuthStateType = {
	triggerTokenValidation: false,
	triggerLogout: false,
	triggerSendEmailVerificationCode: false,

	isLoading: false,

	login: {
		email: '',
		password: '',
	},
	loginErrorMessage: '',

	join: {
		name: '',
		email: '',
		confirmEmailNewsYn: true,
		password: '',
		confirmPassword: '',
	},
	joinErrorMessage: '',

	forgotPassword: {
		email: '',
	},
	forgotPasswordErrorMessage: '',
	showForgotPasswordModal: false,

	customer: new Customer(),
	isLogedin: false,

	updateCustomer: {
		name: '',
		email: '',
		confirmEmailNewsYn: 1,
	},
	isEditCustomerInfo: false,
	updateCustomerErrorMessage: '',
	showCustomerInfoModal: false,
	customerInfoModalInfo: {
		target: '',
		title: '',
	},
	verificationEmail: {
		code: '',
	},

	updatePassword: {
		prevPassword: '',
		newPassword: '',
		confirmPassword: '',
	},
	updateCustomerPasswordErrorMessage: '',
	showCustomerPasswordModal: false,
}

const slice = createSlice({
	name: 'customerAuth',
	initialState,
	reducers: {
		TRIGGER_TOKEN_VALIDATION: (
			state: CustomerAuthStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerTokenValidation = action.payload
		},
		TRIGGER_LOGOUT: (state: CustomerAuthStateType, action: PayloadAction<boolean>) => {
			state.triggerLogout = action.payload
		},
		TRIGGER_SEND_EMAIL_VERIFICATION_CODE: (
			state: CustomerAuthStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerSendEmailVerificationCode = action.payload
		},

		IS_LOADING: (state: CustomerAuthStateType, action: PayloadAction<boolean>) => {
			state.isLoading = action.payload
		},

		LOGIN: (
			state: CustomerAuthStateType,
			action: PayloadAction<CustomerAuthStateType['login']>,
		) => {
			state.login = action.payload
		},
		LOGIN_ERROR_MESSAGE: (
			state: CustomerAuthStateType,
			action: PayloadAction<string>,
		) => {
			state.loginErrorMessage = action.payload
		},

		JOIN: (
			state: CustomerAuthStateType,
			action: PayloadAction<CustomerAuthStateType['join']>,
		) => {
			state.join = action.payload
		},
		JOIN_ERROR_MESSAGE: (state: CustomerAuthStateType, action: PayloadAction<string>) => {
			state.joinErrorMessage = action.payload
		},

		FORGOT_PASSWORD: (
			state: CustomerAuthStateType,
			action: PayloadAction<CustomerAuthStateType['forgotPassword']>,
		) => {
			state.forgotPassword = action.payload
		},
		FORGOT_PASSWORD_ERROR_MESSAGE: (
			state: CustomerAuthStateType,
			action: PayloadAction<string>,
		) => {
			state.forgotPasswordErrorMessage = action.payload
		},
		SHOW_FORGOT_PASSWORD_MODAL: (
			state: CustomerAuthStateType,
			action: PayloadAction<boolean>,
		) => {
			state.showForgotPasswordModal = action.payload
		},

		CUSTOMER: (state: CustomerAuthStateType, action: PayloadAction<CustomerType>) => {
			state.customer = action.payload
		},
		IS_LOGEDIN: (state: CustomerAuthStateType, action: PayloadAction<boolean>) => {
			state.isLogedin = action.payload
		},

		UPDATE_CUSTOMER: (
			state: CustomerAuthStateType,
			action: PayloadAction<CustomerAuthStateType['updateCustomer']>,
		) => {
			state.updateCustomer = action.payload
		},
		IS_EDIT_CUSTOMER_INFO: (
			state: CustomerAuthStateType,
			action: PayloadAction<boolean>,
		) => {
			state.isEditCustomerInfo = action.payload
		},
		UPDATE_CUSTOMER_ERROR_MESSAGE: (
			state: CustomerAuthStateType,
			action: PayloadAction<string>,
		) => {
			state.updateCustomerErrorMessage = action.payload
		},
		SHOW_CUSTOMER_INFO_MODAL: (
			state: CustomerAuthStateType,
			action: PayloadAction<boolean>,
		) => {
			state.showCustomerInfoModal = action.payload
		},
		CUSTOMER_INFO_MODAL_INFO: (
			state: CustomerAuthStateType,
			action: PayloadAction<ModalInfoType>,
		) => {
			state.customerInfoModalInfo = action.payload
		},
		VERIFICATION_EMAIL: (
			state: CustomerAuthStateType,
			action: PayloadAction<CustomerAuthStateType['verificationEmail']>,
		) => {
			state.verificationEmail = action.payload
		},

		UPDATE_PASSWORD: (
			state: CustomerAuthStateType,
			action: PayloadAction<CustomerAuthStateType['updatePassword']>,
		) => {
			state.updatePassword = action.payload
		},
		UPDATE_CUSTOMER_PASSWORD_ERROR_MESSAGE: (
			state: CustomerAuthStateType,
			action: PayloadAction<string>,
		) => {
			state.updateCustomerPasswordErrorMessage = action.payload
		},
		SHOW_CUSTOMER_PASSWORD_MODAL: (
			state: CustomerAuthStateType,
			action: PayloadAction<boolean>,
		) => {
			state.showCustomerPasswordModal = action.payload
		},
	},
})

export const customerAuthActions = slice.actions
export default slice.reducer
